// Esports demo configuration.
//
// demo.esports.statharbour.com is a build-once showcase for the esports
// vertical, mirroring demo.statharbour.com for the ball sports. It points at
// one fully seeded, published league PER TITLE, reachable from a title switcher
// at the root and from per-title deep links (demo.esports.statharbour.com/lol,
// /cs2), so a lead running a Counter-Strike circuit lands on Counter-Strike
// rather than on League of Legends.
//
// The fixed league/season IDs below MUST match the IDs the seed generator emits
// (scripts/generate-esports-demo.mjs -> supabase/seed_esports_demo.sql). They
// use a distinct UUID prefix (0de2...) so they never collide with the sports
// demo data (0de1...).

import type { EsportsGameId } from "@/config/esports/registry";
import { ESPORTS_PROSPECT_DEMOS } from "./esportsProspectDemos";

/** Organization that owns the demo data (shared with the sports demo). */
export const ESPORTS_DEMO_ORG_ID = "0de10000-0000-4000-a000-0000000000a3";

export interface EsportsDemoLeague {
  /** Title id, drives roles, sides, series unit and score noun downstream. */
  game: EsportsGameId;
  /** Deep-link slug under the esports demo host, for example "cs2". */
  slug: string;
  /** Fixed league id (matches the seed SQL). */
  leagueId: string;
  /** Fixed active-season id (matches the seed SQL). */
  seasonId: string;
  /** Default display name shown on the switcher card. */
  displayName: string;
  /** Short tagline for the switcher card. */
  tagline: string;
}

// One demo league per shipped title. Order is the switcher display order, and
// it matches ESPORTS_GAMES in the registry.
export const ESPORTS_DEMO_LEAGUES: EsportsDemoLeague[] = [
  {
    game: "league_of_legends",
    slug: "lol",
    leagueId: "0de20000-0000-4000-a000-0000000000f1",
    seasonId: "0de20000-0000-4000-a000-0000000000f2",
    displayName: "Harbor City Legends League",
    tagline: "Role-based rosters, best-of series, live standings.",
  },
  {
    game: "counter_strike_2",
    slug: "cs2",
    leagueId: "0de20000-0000-4000-a000-0000000000f5",
    seasonId: "0de20000-0000-4000-a000-0000000000f6",
    displayName: "Adriatic Counter-Strike League",
    tagline: "Map-by-map results, CT and T sides, round scores, live timeline.",
  },
  {
    game: "dota_2",
    slug: "dota2",
    leagueId: "0de20000-0000-4000-a000-0000000000f7",
    seasonId: "0de20000-0000-4000-a000-0000000000f8",
    displayName: "Danube Dota Championship",
    tagline: "Numbered-position rosters, Radiant and Dire, kill-scored games.",
  },
];

/** Path to a demo league's public hub page. */
export function esportsDemoLeaguePath(league: EsportsDemoLeague): string {
  return `/esports/public/${league.leagueId}/${league.seasonId}`;
}

/** Look up a demo league by its deep-link slug. */
export function getEsportsDemoLeagueBySlug(
  slug: string,
): EsportsDemoLeague | undefined {
  return ESPORTS_DEMO_LEAGUES.find((l) => l.slug === slug);
}

/** Look up a demo league by title id. */
export function getEsportsDemoLeagueByGame(
  game: string,
): EsportsDemoLeague | undefined {
  return ESPORTS_DEMO_LEAGUES.find((l) => l.game === game);
}

/** True when a league id belongs to one of the esports demo leagues. */
/**
 * True for every demo league: the title demos and the prospect demos
 * (src/config/esportsProspectDemos.ts). Every public esports page marks a
 * demo league noindex through this, so a prospect preview never reaches a
 * search index.
 */
export function isEsportsDemoLeagueId(
  leagueId: string | null | undefined,
): boolean {
  if (!leagueId) return false;
  return (
    ESPORTS_DEMO_LEAGUES.some((l) => l.leagueId === leagueId) ||
    ESPORTS_PROSPECT_DEMOS.some((d) => d.leagueId === leagueId)
  );
}

// Backward-compatible exports. The League of Legends demo was the only one when
// the vertical launched, so its IDs stay the default for anything that expects
// a single esports demo (older emailed links, the previous root redirect).
const LOL_DEMO = getEsportsDemoLeagueByGame("league_of_legends")!;

/** Fixed esports demo league id (League of Legends, the original demo). */
export const ESPORTS_DEMO_LEAGUE_ID = LOL_DEMO.leagueId;

/** Fixed esports demo active-season id (League of Legends). */
export const ESPORTS_DEMO_SEASON_ID = LOL_DEMO.seasonId;

/** Path to the League of Legends demo league's public hub page. */
export function esportsDemoPublicPath(): string {
  return esportsDemoLeaguePath(LOL_DEMO);
}

/** Canonical home of the esports demo experience (the title switcher). */
export const ESPORTS_DEMO_SITE_URL = "https://demo.esports.statharbour.com";
